import React, { useEffect, useState } from "react";

import { Flex, Group } from "@chakra-ui/react";
import { PaginationRoot } from "components/ui/pagination";
import { useUpdateQueryParams } from "hooks/useUpdateQueryParams";
import { useLocation, useSearchParams } from "react-router-dom";
import { TABLE_SCROLL_TO_TOP_OFFSET } from "utils/constants";
import { Maybe } from "utils/general/maybe";

import { CompliancePagination } from "./CompliancePagination";
import PaginationEnd from "./pagination-end";
import PaginationMain from "./pagination-main";
import PaginationStart from "./pagination-start";
import { paginationWrapperCss } from "./style";
import { IPaginationData, IPaginationParams, IPaginationProps } from "./types";

export const PAGE_ONE = 1;
export const DEFAULT_PAGINATION_LIMIT = 10;

export const DEFAULT_PAGINATION_DATA: IPaginationData = {
  limit: 0,
  totalPages: 0,
  pagingCounter: 0,
  hasNextPage: false,
  hasPrevPage: false,
  nextPage: 0,
  prevPage: 0,
  totalDocs: 0,
  page: 0,
};

const PAGINATION_CLICKED_TIMER = 1000;
export const Pagination = (props: IPaginationProps) => {
  const {
    paginationData,
    defaultPaginationParams,
    tableRef,
    isLoadingTable,
    isCompliance,
  } = props;
  const { totalDocs } = paginationData;
  const updateQueryParams = useUpdateQueryParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const defaultPage = defaultPaginationParams?.page || PAGE_ONE;
  const defaultLimit =
    defaultPaginationParams?.limit || DEFAULT_PAGINATION_LIMIT;
  const page = Number(searchParams.get("page") || defaultPage);
  const limit = Number(searchParams.get("limit") || defaultLimit);
  const paginationParams = {
    page,
    limit,
  };
  const startRange = Math.min((page - PAGE_ONE) * limit, totalDocs - PAGE_ONE);
  const endRange =
    startRange + limit > totalDocs ? totalDocs : startRange + limit;

  // Function to determine if the pagination end should be styled as disabled
  const isEndDisabled = endRange >= totalDocs;
  const [isPaginationClicked, setIsPaginationClicked] = useState(false);

  const updatePaginationParams = (paginationParams: IPaginationParams) => {
    const { page, limit } = paginationParams;
    // setPaginationParams(paginationParams);
    const newParams = {
      ...Object.fromEntries(new URLSearchParams(location.search)), // Get existing query params
      page: page.toString(),
      limit: limit.toString(),
    };
    updateQueryParams(newParams);
    // Give time for the loading state to change
    setTimeout(() => {
      setIsPaginationClicked(true);
    }, PAGINATION_CLICKED_TIMER);
  };

  useEffect(() => {
    if (
      !isLoadingTable &&
      isPaginationClicked &&
      tableRef &&
      tableRef.current
    ) {
      window.scrollTo({
        top:
          tableRef.current.getBoundingClientRect().top +
          window.scrollY -
          TABLE_SCROLL_TO_TOP_OFFSET,
        behavior: "smooth",
      });
      setIsPaginationClicked(false);
    }
  }, [isLoadingTable, tableRef, isPaginationClicked]);

  return (
    <Flex
      gap={4}
      my={isCompliance ? "" : "4"}
      px="3"
      alignItems="center"
      justifyContent="space-between"
      w="100%"
      css={paginationWrapperCss}
    >
      <Maybe condition={!!isCompliance}>
        <CompliancePagination
          paginationParams={paginationParams}
          updatePaginationParams={updatePaginationParams}
          paginationData={paginationData}
        />
      </Maybe>
      <Maybe condition={!isCompliance}>
        <PaginationStart
          paginationParams={paginationParams}
          updatePaginationParams={updatePaginationParams}
        />
        <PaginationRoot
          siblingCount={1}
          count={totalDocs}
          pageSize={limit}
          page={page}
          // type={"link"}
          // defaultPage={defaultPage}
          // _currentPage={}
          onPageChange={(evt) => {
            const { page } = evt;
            updatePaginationParams({ page, limit });
          }}
          asChild
        >
          <Group attached>
            <PaginationMain
              paginationParams={paginationParams}
              updatePaginationParams={updatePaginationParams}
              count={totalDocs}
            />
          </Group>
        </PaginationRoot>
        <PaginationEnd
          startRange={startRange + PAGE_ONE}
          endRange={endRange}
          count={totalDocs}
          isEndDisabled={isEndDisabled}
        />
      </Maybe>
    </Flex>
  );
};

export default Pagination;
